import React, { useEffect, useState } from 'react';
import Toast from './components/Toast';
import { BrowserRouter, Navigate, Route, Routes, Link } from 'react-router-dom';
import './App.css';
import { submissionsAPI, authAPI } from './api';

import PublicFeedbackForm from './pages/PublicFeedbackForm';
import AdminLogin from './pages/AdminLogin';
import AdminSubmissions from './pages/AdminSubmissions';
import PasswordResetRequest from './pages/PasswordResetRequest';
import SetNewPassword from './pages/SetNewPassword';

export default function App() {
  const [adminSession, setAdminSession] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [toast, setToast] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const fetchSubmissions = async () => {
    if (!adminSession) return;
    try {
      const response = await submissionsAPI.list();
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (e) {
      console.error("Error fetching submissions: ", e);
    }
  };

  const updateStatus = async (id, status) => {
    if (!adminSession) return;
    try {
      const response = await submissionsAPI.update(id, status);
      if (response.data.success) {
        await fetchSubmissions();
        return;
      }
      throw new Error(response.data.message || 'Status update failed');
    } catch (e) {
      console.error("Error updating submission: ", e);
      throw e;
    }
  };

  const deleteSubmission = async (id) => {
    if (!adminSession) return;
    try {
      const response = await submissionsAPI.delete(id);
      if (response.data.success) {
        await fetchSubmissions();
        return;
      }
      throw new Error(response.data.message || 'Delete failed');
    } catch (e) {
      console.error("Error deleting submission: ", e);
      throw e;
    }
  };

  const onPublicSubmitted = async () => {
    await fetchSubmissions();
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('adminToken');
      setAdminSession(null);
      setSubmissions([]);
      setToast({ type: 'success', message: 'Logged out successfully' });
    } catch (err) {
      console.error("Error signing out: ", err);
      setToast({ type: 'error', message: 'Logout failed' });
    }
  };

  useEffect(() => {
    // Check if user is already logged in from localStorage
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAdminSession({ token });
    }
    setAuthLoading(false);
  }, []);

  // Fetch submissions whenever adminSession changes
  useEffect(() => {
    if (adminSession) {
      fetchSubmissions();
    }
  }, [adminSession]);

  const Header = () => (
    <div className="topNav">
      <div className="topNavInner">
        <div className="brand">
          Feedback<span className="brandDot">.</span>
          <span className="brandLight">Management</span>
        </div>

        <div className="navLinks">
          <Link to="/" className="navLink">
            Public Form
          </Link>
          <Link to="/admin" className="navLink">
            Admin
          </Link>

           {adminSession ? (
              <button
                type="button"
                className="btn btnGhost"
                onClick={handleLogout}
              >
                Logout
              </button>
            ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <BrowserRouter>
      <Header />
      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
      <Routes>
        <Route path="/" element={<PublicFeedbackForm onSubmitted={onPublicSubmitted} />} />

        <Route
          path="/admin"
          element={
            authLoading ? (
              <AdminLogin
                onLogin={(session) => setAdminSession(session)}
                onToast={(t) => setToast(t)}
              />
            ) : adminSession ? (
              <Navigate to="/admin/submissions" replace />
            ) : (
              <AdminLogin
                onLogin={(session) => setAdminSession(session)}
                onToast={(t) => setToast(t)}
              />
            )
          }
        />

        <Route
          path="/admin/submissions"
          element={
            authLoading ? (
              <AdminSubmissions
                submissions={submissions}
                onUpdateStatus={updateStatus}
                onDeleteSubmission={deleteSubmission}
                onRefresh={fetchSubmissions}
                onToast={(t) => setToast(t)}
              />
            ) : adminSession ? (
              <AdminSubmissions
                submissions={submissions}
                onUpdateStatus={updateStatus}
                onDeleteSubmission={deleteSubmission}
                onRefresh={fetchSubmissions}
                onToast={(t) => setToast(t)}
              />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route path="/admin/reset-password" element={<PasswordResetRequest onToast={(t) => setToast(t)} />} />
        <Route path="/admin/set-new-password" element={<SetNewPassword onToast={(t) => setToast(t)} />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
